import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO, { breadcrumbSchema, collectionPageSchema } from '@/components/SEO';
import { ArrowRight, Camera, Filter } from 'lucide-react';
import { motion } from 'framer-motion';
import CTASection from '@/components/CTASection';
import { portfolioCategories, getAllImages } from '@/data/portfolio-data';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const Portfolio = () => {
  const [activeCategory, setActiveCategory] = useState('tout');

  const totalImages = getAllImages().length;

  const visibleCategories = portfolioCategories.filter(
    (cat) => activeCategory === 'tout' || cat.slug === activeCategory
  );

  return (
    <>
      <SEO
        title="Portfolio — Cygnature Production | Nos réalisations à Troyes"
        description="Découvrez en images les événements signés Cygnature Production : mariages, soirées corporate, cérémonies laïques, spectacles artistiques à Troyes et dans toute la France."
        path="/portfolio"
        keywords={['portfolio événementiel','réalisations mariage','galerie événements','photos événements Troyes']}
        jsonLd={[
          breadcrumbSchema([{ name: 'Accueil', path: '/' }, { name: 'Portfolio', path: '/portfolio' }]),
          collectionPageSchema({ name: 'Portfolio', description: 'Galerie de nos réalisations événementielles', path: '/portfolio', itemCount: totalImages }),
        ]}
      />
      {/* Hero */}
      <section className="relative px-6 lg:px-8 py-28 md:py-36 bg-petrol text-petrol-foreground overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-petrol via-petrol/95 to-petrol" />
        <div className="relative max-w-4xl mx-auto text-center z-10">
          <p className="text-xs font-heading font-semibold uppercase tracking-[0.2em] text-primary mb-4">
            Nos réalisations
          </p>
          <h1 className="font-heading text-4xl md:text-6xl font-bold">Portfolio</h1>
          <p className="mt-4 font-heading text-base md:text-lg italic text-primary/80">
            La galerie de l'émotion & des beaux moments
          </p>
          <p className="mt-3 text-sm text-petrol-foreground/60 max-w-xl mx-auto">
            Retour en images sur les événements qui ont façonné Cygnature Production. Chaque projet
            raconte une histoire unique.
          </p>
        </div>
      </section>

      {/* Category filter (Select) */}
      <section className="px-6 lg:px-8 pt-8 pb-2 bg-background">
        <div className="max-w-7xl mx-auto flex justify-center">
          <Select
            value={activeCategory}
            onValueChange={(v) => setActiveCategory(v)}
          >
            <SelectTrigger className="w-full sm:w-auto sm:min-w-[280px] h-11 rounded-xl bg-card border-border text-sm font-heading font-medium px-4 gap-3">
              <Filter size={15} className="text-primary shrink-0" strokeWidth={1.5} />
              <SelectValue />
            </SelectTrigger>
            <SelectContent side="bottom" align="center">
              <SelectItem value="tout">Toutes les catégories ({totalImages})</SelectItem>
              {portfolioCategories.map((cat) => {
                const Icon = cat.icon;
                return (
                  <SelectItem key={cat.slug} value={cat.slug}>
                    <span className="inline-flex items-center gap-2">
                      <Icon size={14} className="text-primary" strokeWidth={1.5} />
                      {cat.name} ({cat.images.length})
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      </section>

      {/* Categories grid */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-background">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {visibleCategories.map((cat, i) => {
              const cover = cat.images[0];
              return (
                <motion.div
                  key={cat.slug}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: Math.min(i * 0.04, 0.3) }}
                >
                  <Link
                    to={`/portfolio/${cat.slug}`}
                    onClick={() => window.scrollTo(0, 0)}
                    className="group block rounded-2xl border border-border bg-card overflow-hidden hover:border-primary/30 transition-colors"
                  >
                    <div className="relative aspect-[16/10] bg-muted overflow-hidden">
                      {cover ? (
                        <img
                          src={cover.src}
                          alt={cover.alt}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          style={{ objectPosition: cat.slug === 'art-de-la-scene' ? 'center 55%' : 'center 25%' }}
                        />
                      ) : (
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <Camera size={28} className="text-primary/30 mb-2" strokeWidth={1.5} />
                          <p className="text-[10px] text-muted-foreground/50 font-heading uppercase tracking-wider">
                            Photos à venir
                          </p>
                        </div>
                      )}
                      <div className="absolute top-3 right-3 bg-petrol/80 text-petrol-foreground text-[10px] font-heading font-semibold px-2.5 py-1 rounded-full">
                        {cat.images.length} photo{cat.images.length > 1 ? 's' : ''}
                      </div>
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2.5 mb-2">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <cat.icon size={16} className="text-primary" strokeWidth={1.5} />
                        </div>
                        <h2 className="font-heading font-bold text-foreground">{cat.name}</h2>
                      </div>
                      <p className="text-xs text-muted-foreground italic mb-3">{cat.tagline}</p>
                      <span className="inline-flex items-center gap-1.5 text-xs text-primary font-heading font-medium group-hover:gap-2.5 transition-all">
                        Voir le dossier <ArrowRight size={12} />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              );
            })}

            {/* "Voir toutes les photos" card */}
            {activeCategory === 'tout' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.35 }}
              >
                <Link
                  to="/portfolio/tous"
                  onClick={() => window.scrollTo(0, 0)}
                  className="group flex flex-col items-center justify-center rounded-2xl border border-dashed border-primary/30 bg-primary/5 hover:bg-primary/10 transition-colors h-full min-h-[280px] p-8 text-center"
                >
                  <Camera size={36} className="text-primary/50 mb-3" strokeWidth={1.5} />
                  <h2 className="font-heading font-bold text-foreground mb-1">
                    Voir toutes les photos
                  </h2>
                  <p className="text-xs text-muted-foreground">{totalImages} réalisations au total</p>
                  <span className="inline-flex items-center gap-1.5 text-xs text-primary font-heading font-medium mt-4 group-hover:gap-2.5 transition-all">
                    Explorer <ArrowRight size={12} />
                  </span>
                </Link>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-petrol text-petrol-foreground">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold">
            Nous orchestrons chaque instant
          </h2>
          <p className="mt-3 font-heading text-sm italic text-petrol-foreground/60">
            Disponible à Troyes, dans l'Aube et partout en France.
          </p>
          <Link
            to="/contact"
            onClick={() => window.scrollTo(0, 0)}
            className="inline-flex items-center gap-2 mt-8 bg-primary text-primary-foreground rounded-full px-8 py-3.5 font-heading font-medium text-sm hover:bg-primary/90 transition-colors"
          >
            Parlons de votre projet <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      <CTASection />
    </>
  );
};

export default Portfolio;
